/**
 * Authentication Context for Coordi Application
 * =============================================
 *
 * Provides authentication state and methods throughout the application.
 * Manages user sessions, profiles, and authentication flows including
 * sign up, sign in, and sign out operations.
 *
 * DEVELOPMENT AUTH BYPASS:
 * =======================
 * This context includes support for development authentication bypass.
 * When VITE_DEV_AUTH_BYPASS=true and valid credentials are configured,
 * the context will automatically sign in on app initialization, bypassing
 * the manual login screen while maintaining full Supabase connectivity.
 *
 * See src/config/devAuth.ts for detailed dev auth documentation.
 *
 * @module AuthContext
 */

import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { UserRole } from '@/integrations/supabase/types';
import { useJWTClaims } from '@/hooks/useJWTClaims';
import { useAnalyticsIdentity } from '@/hooks/useAnalyticsIdentity';
import {
  useSubscriptionLoader,
  type SubscriptionStatus,
} from '@/hooks/useSubscriptionLoader';
import { isDevAuthEnabled, DEV_AUTH_CONFIG, logDevModeStatus } from '@/config/devAuth';
import {
  getCachedAuthState,
  getStaleAuthCache,
  setCachedAuthState,
  clearAuthCache,
  markSessionExpired,
  clearSessionExpiredFlag,
  type CachedProfile,
} from '@/utils/authCache';

/**
 * Extended user profile information from the database
 */
interface UserProfile {
  id: string;
  role: UserRole;
  organization_id: string | null;
  full_name: string | null;
  organizationName: string | null;
}

/**
 * Authentication context value containing user state and auth methods
 */
interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  subscriptionStatus: SubscriptionStatus | null;
  isFounder: boolean;
  isIndividualUser: boolean;  // True if user belongs to a personal organization
  isProfileReady: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  checkFounderAccess: () => boolean;
  consumeTCTokens: (amount?: number, transactionId?: number) => Promise<boolean>;
  refreshSubscription: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Custom hook to access authentication context
 *
 * Provides access to current user, session, profile, and auth methods.
 * Must be used within an AuthProvider.
 *
 * @returns AuthContextType containing user state and auth functions
 * @throws Error if used outside AuthProvider
 *
 * @example
 * const { user, profile, signIn, signOut } = useAuth();
 *
 * if (!user) {
 *   return <LoginForm onSubmit={(email, password) => signIn(email, password)} />;
 * }
 *
 * return (
 *   <div>
 *     <p>Welcome {profile?.full_name}</p>
 *     <button onClick={signOut}>Sign Out</button>
 *   </div>
 * );
 *
 * @remarks
 * - User and session come from Supabase auth
 * - Profile includes organization_id and role from users table
 * - Loading is true during initial auth check or profile fetch
 * - All auth methods handle errors and return { error } for error handling
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 *
 * Wraps the application to provide authentication context to all child components.
 * Manages auth state, listens to auth changes, and loads user profiles.
 *
 * @param props.children - Child components that will have access to auth context
 *
 * @example
 * function App() {
 *   return (
 *     <AuthProvider>
 *       <YourApp />
 *     </AuthProvider>
 *   );
 * }
 *
 * @remarks
 * - Sets up Supabase auth state listener on mount
 * - Loads user profile from database when user authenticates
 * - Automatically cleans up subscriptions on unmount
 * - Profile loading happens after auth state is established
 * - Sets loading states appropriately during auth and profile fetch
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileReady, setProfileReady] = useState(true);

  // Track whether we've already invoked setup_user_profile for this user
  const profileSetupRef = useRef<string | null>(null);
  // Track in-flight setup to prevent duplicate concurrent RPC calls
  const profileSetupInFlightRef = useRef<string | null>(null);
  // Track if we've already initialized from cache to prevent double-init
  const cacheInitializedRef = useRef(false);

  /**
   * Use JWT claims hook to avoid additional profile database queries.
   *
   * Claims provide `organization_id`, `role`, and founder/admin context
   * without requiring an extra round-trip.
   */
  const { claims, loading: claimsLoading } = useJWTClaims();

  /**
   * Derive `profile` from Supabase user and JWT claims.
   * When claims are unavailable or user is null, returns null.
   */
  const profile: UserProfile | null = user && claims ? {
    id: user.id,
    organization_id: claims.organization_id,
    full_name: user.user_metadata?.full_name ?? null,
    role: claims.role,
    organizationName: claims.organization_name,
  } : null;

  /**
   * Founder flag derived from claims for simplified access checks.
   */
  const isFounder = claims?.isFounder ?? false;

  const {
    subscriptionStatus,
    consumeTCTokens,
    refreshSubscription,
  } = useSubscriptionLoader(user, claims);
  useAnalyticsIdentity(user, session);

  /**
   * Ensure the Supabase-side profile is created and onboarding is finished.
   *
   * Calls the `setup_user_profile` RPC which:
   * - Creates a row in `public.users` from auth metadata
   * - Creates a personal organization for individual users
   * - Triggers JWT claim sync + initial trial tokens
   *
   * This function is idempotent – it can be safely called on every sign-in.
   *
   * Always invoke the server-side self-heal once per authenticated session.
   * New signup deal grants now rely on this path, so local JWT/cache shortcuts
   * must not bypass the RPC just because org claims already exist.
   */
  const runSetupUserProfile = useCallback(async (currentUser: User) => {
    // Avoid re-running for the same user if we've already completed setup
    if (
      profileSetupRef.current === currentUser.id ||
      profileSetupInFlightRef.current === currentUser.id
    ) {
      console.debug('[AuthContext] Skipping setup_user_profile - already completed for:', currentUser.id);
      return;
    }

    console.log('[AuthContext] runSetupUserProfile starting for:', currentUser.id);
    
    try {
      profileSetupInFlightRef.current = currentUser.id;
      setProfileReady(false);
      console.debug('[AuthContext] Calling setup_user_profile RPC for user:', currentUser.id);

      // Reduced timeout: 3s instead of 10s (most setups complete in <1s)
      const timeoutPromise = new Promise<{ data: null; error: Error }>((_, reject) => 
        setTimeout(() => reject(new Error('setup_user_profile timed out after 3s')), 3000)
      );
      
      const rpcPromise = supabase.rpc('setup_user_profile');
      
      const { data, error } = await Promise.race([rpcPromise, timeoutPromise]);

      if (error) {
        console.error('[AuthContext] setup_user_profile error:', error);
      } else {
        console.debug('[AuthContext] setup_user_profile result:', data);
        
        // Refresh the session to get updated JWT claims (including organization_id)
        // This is critical - without this, the JWT token has stale claims
        console.debug('[AuthContext] Refreshing session to get updated JWT claims...');
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          console.error('[AuthContext] Session refresh error:', refreshError);
        } else {
          console.debug('[AuthContext] Session refreshed successfully with new claims');
        }
      }
    } catch (err) {
      console.error('[AuthContext] Exception calling setup_user_profile:', err);
    } finally {
      console.log('[AuthContext] runSetupUserProfile finished, setting profileReady=true');
      profileSetupRef.current = currentUser.id;
      profileSetupInFlightRef.current = null;
      setProfileReady(true);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    /**
     * Initialize authentication on app startup.
     *
     * This function handles:
     * 1. Cache-first loading for instant UX (stale-while-revalidate pattern)
     * 2. Development auto-login bypass
     * 3. Normal session retrieval and validation
     *
     * The order is important: cache check → dev auth → normal session retrieval.
     */
    const initializeAuth = async () => {
      // ============================================================================
      // CACHE-FIRST LOADING - Instant UX for returning users
      // ============================================================================
      // Check localStorage cache before making any network calls.
      // If we have cached auth state, render immediately while validating in background.
      if (!cacheInitializedRef.current) {
        cacheInitializedRef.current = true;
        
        const cachedState = getCachedAuthState();
        if (cachedState?.user && cachedState?.session) {
          console.log('[AuthContext] Found valid cache, rendering immediately');
          setSession(cachedState.session);
          setUser(cachedState.user);
          setAuthLoading(false);
          
          // Mark profile as already set up if cache has org_id
          if (cachedState.profile?.organization_id) {
            profileSetupRef.current = cachedState.user.id;
          }
          
          // Background validation will happen via onAuthStateChange when
          // Supabase SDK initializes and checks the stored session.
          // If session is invalid, onAuthStateChange will fire with SIGNED_OUT.
        }
      }

      // ============================================================================
      // DEVELOPMENT AUTH BYPASS - Only active in development mode
      // ============================================================================
      // This feature allows developers to bypass manual authentication while
      // maintaining full Supabase connectivity, RLS policies, and real user data.
      //
      // Safety checks:
      // - Only works in development (!import.meta.env.PROD)
      // - Requires VITE_DEV_AUTH_BYPASS=true
      // - Requires valid email/password credentials
      // - Falls back to normal auth flow on failure
      // ============================================================================
      if (isDevAuthEnabled()) {
        logDevModeStatus(); // Show colorful dev mode indicator in console

        // Check current session to avoid redundant sign-in attempts
        const { data: { session: existingSession } } = await supabase.auth.getSession();

        if (!existingSession) {
          console.log('[AuthContext] DEV MODE: Auto signing in...');

          // Perform auto-login using configured staging credentials
          const { data, error } = await supabase.auth.signInWithPassword({
            email: DEV_AUTH_CONFIG.email,
            password: DEV_AUTH_CONFIG.password,
          });

          if (error) {
            console.error('[AuthContext] DEV MODE: Auto sign-in failed:', error.message);
            console.error('Check VITE_DEV_USER_EMAIL and VITE_DEV_USER_PASSWORD in .env.local');
            console.error('Ensure the dev user exists in staging Supabase');
          } else {
            console.log('[AuthContext] DEV MODE: Auto signed in as', data.user?.email);
            console.log('[AuthContext] DEV MODE: Full Supabase connectivity active with real JWT');
          }
        } else {
          console.log('[AuthContext] DEV MODE: Already signed in as', existingSession.user?.email);
        }
      }

      // ============================================================================
      // NORMAL AUTH INITIALIZATION - Always runs regardless of dev mode
      // ============================================================================
      // Retrieve current session and update auth state
      // This works whether user signed in manually or via dev auto-login
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (isMounted) {
        // Check if we had a cached session but Supabase says it's invalid
        // This indicates the session expired while the user was away
        const hadCachedSession = getStaleAuthCache()?.user !== null;
        const sessionInvalid = sessionError || !session;
        
        if (hadCachedSession && sessionInvalid) {
          console.log('[AuthContext] Cached session was invalid, marking as expired');
          markSessionExpired();
          clearAuthCache();
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setAuthLoading(false);
        
        // Update cache with fresh session data
        if (session?.user) {
          const profileData: CachedProfile | null = session.user.app_metadata ? {
            id: session.user.id,
            role: session.user.app_metadata.role || 'user',
            organization_id: session.user.app_metadata.organization_id || null,
            full_name: session.user.user_metadata?.full_name || null,
            organizationName: session.user.app_metadata.organization_name || null,
          } : null;
          setCachedAuthState(session.user, session, profileData);
        }
      }
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[AuthContext] onAuthStateChange event:', event, 'hasSession:', !!session);

        if (!isMounted) {
          console.log('[AuthContext] Component unmounted, ignoring auth state change');
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        setAuthLoading(false);

        // On sign-in (or initial session if we haven't processed this user), ensure the profile is fully set up
        const shouldRunSetup =
          session?.user &&
          (event === 'SIGNED_IN' ||
            (event === 'INITIAL_SESSION' && profileSetupRef.current !== session.user.id));

        if (shouldRunSetup) {
          try {
            await runSetupUserProfile(session.user);
            
            // Update cache after successful setup (session may have fresh claims)
            if (session?.user) {
              const profileData: CachedProfile | null = session.user.app_metadata ? {
                id: session.user.id,
                role: session.user.app_metadata.role || 'user',
                organization_id: session.user.app_metadata.organization_id || null,
                full_name: session.user.user_metadata?.full_name || null,
                organizationName: session.user.app_metadata.organization_name || null,
              } : null;
              setCachedAuthState(session.user, session, profileData);
              // Clear any stale session expired flag on successful sign-in
              clearSessionExpiredFlag();
            }
          } catch (err) {
            console.error('[AuthContext] Error in runSetupUserProfile:', err);
            // Ensure we don't get stuck in loading state
            if (isMounted) {
              setProfileReady(true);
            }
          }
        }

        // On token refresh, update the cache with fresh session
        if (event === 'TOKEN_REFRESHED' && session?.user) {
          console.debug('[AuthContext] Token refreshed, updating cache');
          const profileData: CachedProfile | null = session.user.app_metadata ? {
            id: session.user.id,
            role: session.user.app_metadata.role || 'user',
            organization_id: session.user.app_metadata.organization_id || null,
            full_name: session.user.user_metadata?.full_name || null,
            organizationName: session.user.app_metadata.organization_name || null,
          } : null;
          setCachedAuthState(session.user, session, profileData);
        }

        // On sign-out, reset profile onboarding state and subscription caches
        // Note: We clear cache but do NOT mark session as expired (user intentionally signed out)
        if (event === 'SIGNED_OUT') {
          profileSetupRef.current = null;
          setProfileReady(true);
          clearAuthCache();
          cacheInitializedRef.current = false;
        }
      }
    );

    // Initialize auth
    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [runSetupUserProfile]);

  const signUp = async (email: string, password: string, fullName?: string) => {
    // Set the redirect URL to our email confirmation page
    const redirectUrl = `${window.location.origin}/email-confirmation`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName
        }
      }
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const checkFounderAccess = (): boolean => {
    return isFounder;
  };

  // Derive isIndividualUser from JWT claims (is_personal flag)
  const isIndividualUser = claims?.is_personal || claims?.organization_id === null;

  // Debug loading state
  const loading = authLoading || claimsLoading || (user !== null && !profileReady);
  
  useEffect(() => {
    console.log('[AuthContext] Loading state debug:', {
      loading,
      authLoading,
      claimsLoading,
      profileReady,
      hasUser: !!user,
      hasClaims: !!claims,
    });
  }, [loading, authLoading, claimsLoading, profileReady, user, claims]);

  const value = {
    user,
    session,
    profile,
    subscriptionStatus,
    isFounder,
    isIndividualUser,
    isProfileReady: profileReady,
    signUp,
    signIn,
    signOut,
    checkFounderAccess,
    consumeTCTokens,
    refreshSubscription,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
